import { DateObjectUnits, DateTime } from 'luxon';
import { TDateTimeRangeQueryValue } from '../../../types/shared/TDateTimeRangeQueryValue';
import { TDateRangeQueryValue } from '../../../types/shared/TDateRangeQueryValue';

/**
 * Appends time data to a date range.  The From/To dates are expected to be in ISODateString format.  An example use
 * case is if you want to talk to an endpoint that requires timestamps, but your means of input is <DateRangeFilter>,
 * which doesn't provide time selection.  You pass in the From/To times to append to the dates.  If time data is not
 * provided, defaults are applied:  the From time will be 00:00:00.000 and the To time will be 23:59:59.999.
 *
 * @param dates Array<ISODateString>(2), with [0] as the From date and [1] as the To date
 * @param times Array<DateObjectUnits>(2), with [0] as the From time and [1] as the To time.  If not provided, defaults of 00:00:00.0000 and 23:59:59.999 are used.  These default belong to Money Flow and we rely on them.
 *
 * @throws If {dates} is not Array<ISODateString>(2)
 *
 * @return Array<ISODateTimeString>(2), with [0] as the From datetime and [1] as the To datetime
 */
export const convertISODateRangeToISODateTimeRange = (
  dates: TDateRangeQueryValue,
  times: [DateObjectUnits, DateObjectUnits] = [
    {
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    {
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    },
  ]
): TDateTimeRangeQueryValue => {
  const notDates = ['30', '60', '90']; // If it's a relative date, we're gonna bail

  if (
    !dates ||
    !dates.length ||
    dates.length !== 2 ||
    notDates.indexOf(dates[0]) >= 0 ||
    notDates.indexOf(dates[1]) >= 0 ||
    dates[0].indexOf(':') >= 0 || // also bail if it's already a datetime
    dates[1].indexOf(':') >= 0
  ) {
    throw new Error(`Conversion from ISODate to ISODateTime:  input [${dates}] not in the expected format`);
  }

  return [DateTime.fromISO(dates[0]).set(times[0]).toISO(), DateTime.fromISO(dates[1]).set(times[1]).toISO()];
};
